import React from 'react';
import { cn } from '@/utils/cn';

export interface TeeLoaderProps {
  /**
   * Size of the loader
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Loading text to display
   */
  text?: string;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Full screen overlay
   */
  fullScreen?: boolean;
}

const sizeClasses = {
  sm: 'w-6 h-6',
  md: 'w-10 h-10',
  lg: 'w-16 h-16',
};

const TeeLoader = React.forwardRef<HTMLDivElement, TeeLoaderProps>(
  ({ size = 'md', text, className, fullScreen = false }, ref) => {
    const loaderContent = (
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-6',
          className
        )}
      >
        <div className={cn('relative', sizeClasses[size])}>
          <svg
            className="w-full h-full animate-spin"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="15.7 62.8"
              strokeLinecap="round"
              /* Forest, not white: every surface in this theme is cream, so a
                 white spinner was invisible everywhere except the full-screen
                 overlay it was originally written for. */
              className="text-[var(--color-forest)]"
            />
          </svg>
        </div>
        {text && (
          <p className="font-[family-name:var(--type-font-family-heading,'Inter:medium',sans-serif)] font-medium text-[var(--font-size-heading-h3,24px)] leading-[var(--line-height-heading-h3,28px)] text-[var(--text-content-title)]">
            {text}
          </p>
        )}
      </div>
    );

    if (fullScreen) {
      return (
        <div
          ref={ref}
          className="fixed inset-0 flex items-center justify-center bg-[var(--background-card)]/80 z-50"
        >
          {loaderContent}
        </div>
      );
    }

    return (
      <div ref={ref} className="flex items-center justify-center py-8">
        {loaderContent}
      </div>
    );
  }
);

TeeLoader.displayName = 'TeeLoader';

export { TeeLoader };
export default TeeLoader;
